import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Download, Printer, FileText } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface CoursePdfExportProps {
  courseId: string;
  courseTitle: string;
  lessons: any[];
  className?: string;
}

const CoursePdfExport: React.FC<CoursePdfExportProps> = ({
  courseId,
  courseTitle = "Khóa học Phật giáo",
  lessons,
  className,
}) => {
  const { t } = useTranslation("courses");
  const [isExporting, setIsExporting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const backgroundImage = "https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/1e56d312-0674-4678-e4c0-2b0eae7ee500/public";
  const cornerImage = "https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public";

  // Thêm hàm xử lý nội dung Markdown và HTML
  const processContent = (content: string): string => {
    if (!content) return t("pdf_export.no_content");
    
    // Kiểm tra nếu nội dung đã là HTML
    if (content.includes('<') && content.includes('>')) {
      // Đã là HTML, chỉ cần làm sạch các thẻ script và một số thẻ không cần thiết và thêm style
      let html = content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<button\b[^<]*(?:(?!<\/button>)<[^<]*)*<\/button>/gi, '');
      
      // Thêm style để điều chỉnh khoảng cách dòng và font chữ
      html = html
        .replace(/<p/g, '<p style="line-height: 1.3; margin-bottom: 0.8em; font-family: \'Georgia\', serif;"')
        .replace(/<li/g, '<li style="margin-bottom: 5px; font-family: \'Georgia\', serif;"')
        .replace(/<ul/g, '<ul style="padding-left: 20px; margin: 10px 0;"')
        .replace(/<ol/g, '<ol style="padding-left: 20px; margin: 10px 0;"')
        .replace(/<h1/g, '<h1 style="font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;"')
        .replace(/<h2/g, '<h2 style="font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;"')
        .replace(/<h3/g, '<h3 style="font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;"')
        .replace(/<h4/g, '<h4 style="font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;"')
        .replace(/<h5/g, '<h5 style="font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;"')
        .replace(/<h6/g, '<h6 style="font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;"');
      
      return html;
    }
    
    // Nếu là Markdown, chuyển đổi thành HTML cơ bản
    // Thay thế heading
    let html = content
      .replace(/^# (.*$)/gim, '<h1 style="font-size: 24px; margin: 20px 0 10px; line-height: 1.2; font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;">$1</h1>')
      .replace(/^## (.*$)/gim, '<h2 style="font-size: 20px; margin: 15px 0 10px; line-height: 1.2; font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 style="font-size: 18px; margin: 15px 0 10px; line-height: 1.2; font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;">$1</h3>')
      .replace(/^#### (.*$)/gim, '<h4 style="font-size: 16px; margin: 15px 0 10px; line-height: 1.2; font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;">$1</h4>')
      .replace(/^##### (.*$)/gim, '<h5 style="font-size: 14px; margin: 15px 0 10px; line-height: 1.2; font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;">$1</h5>')
      .replace(/^###### (.*$)/gim, '<h6 style="font-size: 14px; margin: 15px 0 10px; line-height: 1.2; font-family: \'Palatino Linotype\', \'Book Antiqua\', Palatino, serif;">$1</h6>');
    
    // Thay thế in đậm và in nghiêng
    html = html
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Xử lý danh sách tốt hơn bằng cách tạo thẻ ul/ol thích hợp
    // Tìm các dòng bắt đầu với dấu gạch đầu dòng hoặc số
    const bulletListMatches = html.match(/^(\s*[\-\+\*]\s+.+)$/gm);
    const numberListMatches = html.match(/^(\s*\d+\.\s+.+)$/gm);
    
    if (bulletListMatches) {
      bulletListMatches.forEach(match => {
        const item = match.replace(/^\s*[\-\+\*]\s+(.+)$/m, '<li style="margin-bottom: 5px; font-family: \'Georgia\', serif;">$1</li>');
        html = html.replace(match, item);
      });
      
      // Bọc các li liên tiếp trong thẻ ul
      html = html.replace(/(<li[^>]*>.*?<\/li>)(\s*)(<li[^>]*>)/g, '$1$2$3');
      html = html.replace(/(<li[^>]*>.*?<\/li>)(?!\s*<li)/g, '<ul style="padding-left: 20px; margin: 10px 0;">$1</ul>');
    }
    
    if (numberListMatches) {
      numberListMatches.forEach(match => {
        const item = match.replace(/^\s*\d+\.\s+(.+)$/m, '<li style="margin-bottom: 5px; font-family: \'Georgia\', serif;">$1</li>');
        html = html.replace(match, item);
      });
      
      // Bọc các li liên tiếp trong thẻ ol
      html = html.replace(/(<li[^>]*>.*?<\/li>)(\s*)(<li[^>]*>)/g, '$1$2$3');
      html = html.replace(/(<li[^>]*>.*?<\/li>)(?!\s*<li)/g, '<ol style="padding-left: 20px; margin: 10px 0;">$1</ol>');
    }
    
    // Thay thế liên kết
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" style="color: #0066cc; text-decoration: underline; font-family: \'Georgia\', serif;">$1</a>');
    
    // Xử lý đoạn văn
    html = html.replace(/^([^<\n].+)$/gim, '<p style="margin-bottom: 0.8em; line-height: 1.3; font-family: \'Georgia\', serif;">$1</p>');
    
    // Thay thế dòng trống thành khoảng cách đoạn văn
    html = html.replace(/\n\s*\n/g, '</p><p style="margin-bottom: 0.8em; line-height: 1.3; font-family: \'Georgia\', serif;">');
    
    // Xóa bỏ thẻ p rỗng
    html = html.replace(/<p[^>]*>\s*<\/p>/g, '');
    
    return html;
  };

  // Xử lý xuất PDF
  const handleExportPdf = async () => {
    if (!contentRef.current || lessons.length === 0) {
      toast.error(t("pdf_export.error_empty"));
      return;
    }

    setIsExporting(true);

    try {
      // Hiển thị thông báo đang xuất PDF
      toast.info(t("pdf_export.preparing"));

      // Tạo một bản sao của contentRef để chỉnh sửa mà không ảnh hưởng đến UI
      const content = contentRef.current.cloneNode(true) as HTMLElement;
      
      // Đảm bảo nội dung hiển thị đầy đủ trong PDF
      content.style.width = "800px";
      content.style.height = "auto";
      content.style.position = "absolute";
      content.style.top = "0";
      content.style.left = "0";
      content.style.zIndex = "-9999";
      content.style.backgroundColor = "white";
      
      // Ẩn các nút không cần thiết trong PDF
      const buttonsToHide = content.querySelectorAll("button");
      buttonsToHide.forEach((button) => {
        button.style.display = "none";
      });
      
      document.body.appendChild(content);

      // Tạo PDF A4
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Thêm trang bìa
      await addCoverPage(pdf, courseTitle);

      // Thêm trang mục lục
      addTableOfContents(pdf, lessons);

      // Thêm các bài học
      for (let i = 0; i < lessons.length; i++) {
        const lesson = lessons[i];
        
        // Tạo một phần tử mới chứa nội dung bài học
        const lessonElement = document.createElement("div");
        lessonElement.innerHTML = `
          <div style="padding: 30px 20px; font-family: 'Georgia', serif; line-height: 1.5;">
            <h2 style="color: #333; margin-bottom: 15px; font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif;">${i + 1}. ${lesson.name}</h2>
            <div style="line-height: 1.5;">${processContent(lesson.content)}</div>
          </div>
        `;
        
        document.body.appendChild(lessonElement);
        
        // Chuyển đổi nội dung bài học thành canvas
        const canvas = await html2canvas(lessonElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });
        
        // Xóa phần tử tạm
        document.body.removeChild(lessonElement);
        
        // Thêm trang mới cho mỗi bài học
        if (i > 0) pdf.addPage();
        
        // Thêm hình nền và trang trí 4 góc
        addPageDecoration(pdf);
        
        // Thêm tiêu đề bài học
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text(`${i + 1}. ${lesson.name}`, 20, 25);
        
        // Thêm nội dung bài học
        const imgData = canvas.toDataURL("image/jpeg", 0.8);
        const imgWidth = pdf.internal.pageSize.getWidth() - 40; // Margin 20mm on each side
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Kiểm tra xem nội dung có vượt quá trang không
        const pageHeight = pdf.internal.pageSize.getHeight();
        let heightLeft = imgHeight;
        let position = 35; // Tăng margin top để tạo khoảng cách sau header
        
        // Thêm hình ảnh vào PDF với vị trí thích hợp
        pdf.addImage(imgData, "JPEG", 20, position, imgWidth, imgHeight);
        
        heightLeft -= (pageHeight - 50); // Tăng margin bottom để tránh đè vào footer
        
        // Nếu nội dung dài, thêm các trang tiếp theo
        while (heightLeft > 0) {
          position = 25; // Reset lại vị trí trên trang mới với margin top lớn hơn
          pdf.addPage();
          addPageDecoration(pdf);
          pdf.addImage(imgData, "JPEG", 20, position - imgHeight + heightLeft, imgWidth, imgHeight);
          heightLeft -= (pageHeight - 50); // Tăng margin để tránh đè vào footer
        }
      }

      // Xóa phần tử tạm
      document.body.removeChild(content);

      // Lưu PDF
      pdf.save(`khoa-hoc-${courseId}.pdf`);
      
      toast.success(t("pdf_export.success"));
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error(t("pdf_export.error"));
    } finally {
      setIsExporting(false);
    }
  };

  // Thêm trang bìa cho PDF
  const addCoverPage = async (pdf: any, title: string) => {
    // Tạo phần tử HTML cho trang bìa
    const coverElement = document.createElement("div");
    coverElement.style.width = "800px";
    coverElement.style.height = "1120px"; // Tương đương A4
    coverElement.style.display = "flex";
    coverElement.style.flexDirection = "column";
    coverElement.style.justifyContent = "center";
    coverElement.style.alignItems = "center";
    coverElement.style.position = "relative";
    coverElement.style.overflow = "hidden";
    
    // Thêm background
    coverElement.style.backgroundImage = `url(${backgroundImage})`;
    coverElement.style.backgroundSize = "cover";
    coverElement.style.backgroundPosition = "center";
    
    // Thêm logo và trang trí 4 góc
    coverElement.innerHTML = `
      <div style="position: absolute; top: 20px; left: 20px; width: 80px;">
        <img src="${cornerImage}" style="width: 100%; opacity: 0.4;" />
      </div>
      <div style="position: absolute; top: 20px; right: 20px; width: 80px;">
        <img src="${cornerImage}" style="width: 100%; opacity: 0.4;" />
      </div>
      <div style="position: absolute; bottom: 20px; left: 20px; width: 80px;">
        <img src="${cornerImage}" style="width: 100%; opacity: 0.4;" />
      </div>
      <div style="position: absolute; bottom: 20px; right: 20px; width: 80px;">
        <img src="${cornerImage}" style="width: 100%; opacity: 0.4;" />
      </div>
      <div style="text-align: center; padding: 20px; background-color: rgba(255,255,255,0.8); border-radius: 10px; margin: 20px;">
        <h1 style="font-size: 32px; margin-bottom: 20px; color: #333; line-height: 1.2; font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif;">${title}</h1>
        <img src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a1d82958-cc6f-4bdc-ce0b-25bdc7b27500/public" style="width: 200px; margin: 20px 0;" />
        <p style="margin-top: 20px; font-size: 18px; line-height: 1.2; font-family: 'Georgia', serif;">Giác Ngộ - giacngo.org</p>
      </div>
    `;
    
    document.body.appendChild(coverElement);
    
    // Chuyển đổi trang bìa thành canvas
    const canvas = await html2canvas(coverElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });
    
    // Xóa phần tử tạm
    document.body.removeChild(coverElement);
    
    // Thêm trang bìa vào PDF
    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const imgWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = pdf.internal.pageSize.getHeight();
    
    pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
    pdf.addPage();
  };

  // Thêm mục lục
  const addTableOfContents = (pdf: any, lessons: any[]) => {
    // Thêm tiêu đề
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.text(t("pdf_export.table_of_contents"), 105, 20, { align: "center" });
    
    // Thêm các mục
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    
    // Thêm trang trí
    addPageDecoration(pdf);
    
    // Thêm danh sách bài học
    lessons.forEach((lesson, index) => {
      const y = 40 + (index * 10);
      
      if (y > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        addPageDecoration(pdf);
        return; // Tiếp tục với trang mới
      }
      
      pdf.text(`${index + 1}. ${lesson.name}`, 20, y);
    });
  };

  // Thêm trang trí cho mỗi trang
  const addPageDecoration = (pdf: any) => {
    // Lấy kích thước trang
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Thêm hình nền mờ
    pdf.setFillColor(255, 248, 230);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");
    
    // Thêm header và footer
    const headerHeight = 15;
    const footerHeight = 15;
    
    // Thêm header nhẹ nhàng
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(20, headerHeight, pageWidth - 20, headerHeight);
    
    // Thêm footer nhẹ nhàng
    pdf.line(20, pageHeight - footerHeight, pageWidth - 20, pageHeight - footerHeight);
    
    // Thêm số trang ở footer
    pdf.setFont("georgia", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    const pageInfo = `${pdf.getCurrentPageInfo().pageNumber}/${pdf.getNumberOfPages()}`;
    const currentDate = new Date().toLocaleDateString('vi-VN');
    
    pdf.text(currentDate, 20, pageHeight - 10);
    pdf.text("giacngo.org", pageWidth / 2, pageHeight - 10, { align: "center" });
    pdf.text(pageInfo, pageWidth - 20, pageHeight - 10, { align: "right" });
    
    // Thêm hình trang trí 4 góc (không xoay)
    const cornerWidth = 15; // chiều rộng cố định
    
    // Lưu trạng thái hiện tại của pdf
    pdf.saveGraphicsState();
    
    // Tính tỷ lệ giữ nguyên chiều rộng cho ảnh
    const cornerImg = new Image();
    cornerImg.src = cornerImage;
    const aspectRatio = cornerImg.height / cornerImg.width;
    const cornerHeight = cornerWidth * aspectRatio;
    
    // Set opacity cho images (40%)
    pdf.setGState(new pdf.GState({ opacity: 0.4 }));
    
    // Góc trên bên trái
    pdf.addImage(cornerImage, "PNG", 5, 5, cornerWidth, cornerHeight);
    
    // Góc trên bên phải
    pdf.addImage(cornerImage, "PNG", pageWidth - cornerWidth - 5, 5, cornerWidth, cornerHeight);
    
    // Góc dưới bên trái
    pdf.addImage(cornerImage, "PNG", 5, pageHeight - cornerHeight - 5, cornerWidth, cornerHeight);
    
    // Góc dưới bên phải
    pdf.addImage(cornerImage, "PNG", pageWidth - cornerWidth - 5, pageHeight - cornerHeight - 5, cornerWidth, cornerHeight);
    
    // Khôi phục trạng thái
    pdf.restoreGraphicsState();
  };

  // Xử lý in khóa học
  const handlePrint = () => {
    // Mở cửa sổ in mới
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Không thể mở cửa sổ in. Vui lòng kiểm tra trình chặn popup của trình duyệt.");
      return;
    }

    const currentDate = new Date().toLocaleDateString('vi-VN');

    // Chuẩn bị nội dung HTML để in
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${courseTitle}</title>
        <meta charset="utf-8">
        <style>
          @page {
            margin: 30mm 20mm 30mm 20mm;
          }
          
          body {
            font-family: 'Georgia', serif;
            line-height: 1.5;
            background-color: #fff;
            background-image: url(${backgroundImage});
            background-size: cover;
            background-position: center;
            background-attachment: fixed;
            color: #333;
            margin: 0;
            padding: 0;
          }
          
          .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background-color: rgba(255, 255, 255, 0.9);
            box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
            position: relative;
          }
          
          .corner {
            position: absolute;
            width: 60px;
            height: auto;
            opacity: 0.4;
          }
          
          .corner-top-left {
            top: 10px;
            left: 10px;
          }
          
          .corner-top-right {
            top: 10px;
            right: 10px;
          }
          
          .corner-bottom-left {
            bottom: 10px;
            left: 10px;
          }
          
          .corner-bottom-right {
            bottom: 10px;
            right: 10px;
          }
          
          .cover {
            text-align: center;
            padding: 50px 0;
            margin-bottom: 40px;
          }
          
          .logo {
            width: 150px;
            margin-bottom: 20px;
          }
          
          h1 {
            font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif;
            font-size: 28px;
            margin-bottom: 10px;
            color: #333;
            line-height: 1.2;
          }
          
          h2 {
            font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif;
            font-size: 22px;
            margin: 25px 0 15px;
            color: #444;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
            line-height: 1.2;
          }
          
          p {
            margin-bottom: 0.8em;
          }
          
          .content {
            margin-bottom: 30px;
          }
          
          .toc {
            margin: 40px 0;
          }
          
          .toc-item {
            margin-bottom: 8px;
          }
          
          .lesson {
            margin-bottom: 40px;
            page-break-after: always;
            padding: 0 10px;
          }
          
          .lesson-title {
            font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif;
            font-size: 24px;
            margin-bottom: 20px;
            color: #222;
            line-height: 1.2;
          }
          
          .lesson-content {
            margin-top: 15px;
            line-height: 1.5;
          }
          
          img {
            max-width: 100%;
            height: auto;
          }
          
          ul, ol {
            padding-left: 20px;
            margin: 15px 0;
          }
          
          li {
            margin-bottom: 8px;
          }
          
          .header, .footer {
            padding: 5px 0;
            text-align: center;
            color: #777;
            font-size: 9pt;
            font-style: italic;
          }
          
          .header {
            border-bottom: 1px solid #eee;
            margin-bottom: 20px;
          }
          
          .footer {
            border-top: 1px solid #eee;
            margin-top: 20px;
            position: relative;
            height: 20px;
          }
          
          .footer .date {
            position: absolute;
            left: 10px;
          }
          
          .footer .site {
            position: absolute;
            left: 0;
            right: 0;
            text-align: center;
          }
          
          .footer .page:after {
            content: counter(page);
          }
          
          .footer .page {
            position: absolute;
            right: 10px;
          }
          
          @media print {
            body {
              background-image: url(${backgroundImage}) !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .container {
              box-shadow: none;
            }
            
            .lesson {
              page-break-after: always;
            }
            
            .header, .footer {
              display: block !important;
            }
            
            @page {
              @bottom-center {
                content: "giacngo.org";
              }
              @bottom-right {
                content: counter(page);
              }
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- 4 Corner Images (không xoay, opacity 40%) -->
          <img src="${cornerImage}" class="corner corner-top-left" alt="Corner decoration" />
          <img src="${cornerImage}" class="corner corner-top-right" alt="Corner decoration" />
          <img src="${cornerImage}" class="corner corner-bottom-left" alt="Corner decoration" />
          <img src="${cornerImage}" class="corner corner-bottom-right" alt="Corner decoration" />
          
          <!-- Cover Page -->
          <div class="cover">
            <img src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a1d82958-cc6f-4bdc-ce0b-25bdc7b27500/public" alt="Giác Ngộ" class="logo" />
            <h1>${courseTitle}</h1>
            <p>Giác Ngộ - giacngo.org</p>
          </div>
          
          <!-- Table of Contents -->
          <div class="toc">
            <h2>Mục Lục</h2>
            ${lessons.map((lesson, index) => 
              `<div class="toc-item">${index + 1}. ${lesson.name}</div>`
            ).join('')}
          </div>
          
          <!-- Lessons -->
          ${lessons.map((lesson, index) => 
            `<div class="lesson">
              <div class="header">
                ${currentDate}
              </div>
              <h2 class="lesson-title">${index + 1}. ${lesson.name}</h2>
              <div class="lesson-content">${processContent(lesson.content)}</div>
              <div class="footer">
                <span class="date">${currentDate}</span>
                <span class="site">giacngo.org</span>
                <span class="page"></span>
              </div>
            </div>`
          ).join('')}
        </div>
      </body>
      </html>
    `;

    // Viết nội dung vào cửa sổ mới
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Đợi tải hình ảnh xong
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  return (
    <div className={cn("flex flex-col", className)}>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-2 justify-between items-center">
            <div className="flex items-center">
              <FileText className="mr-2 h-5 w-5 text-muted-foreground" />
              <span>Công cụ</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={isExporting || lessons.length === 0}
              >
                <Printer className="mr-2 h-4 w-4" />
                In khóa học
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleExportPdf}
                disabled={isExporting || lessons.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                Tải PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Phần nội dung khóa học ẩn để tạo PDF */}
      <div ref={contentRef} className="hidden">
        {lessons.map((lesson, index) => (
          <div key={lesson.id} className="mb-8">
            <h2 className="text-xl font-bold mb-2">{index + 1}. {lesson.name}</h2>
            <div dangerouslySetInnerHTML={{ __html: processContent(lesson.content) }} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoursePdfExport; 